import { supabase } from '../client'
import type { CloudTimeEntry } from '../types'
import { logSupabaseError } from './logSupabaseError'

/* ── TIME ENTRIES (Fokuszeit) ──────────────────────────── */

export async function getCloudTimeEntries(userId: string, since?: Date): Promise<CloudTimeEntry[]> {
  if (!supabase) return []
  try {
    let query = supabase
      .from('time_entries')
      .select('*')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .order('started_at', { ascending: true })
    if (since) query = query.gte('updated_at', since.toISOString())
    const { data, error } = await query
    if (error) {
      logSupabaseError('Get time entries error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get time entries error', error)
    return []
  }
}

export async function getCloudTimeEntryIds(userId: string): Promise<string[]> {
  if (!supabase) return []
  try {
    const { data, error } = await supabase
      .from('time_entries')
      .select('id')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .limit(100000)
    if (error) {
      logSupabaseError('Get time entry IDs error', error)
      return []
    }
    return (data || []).map(row => row.id)
  } catch (error) {
    logSupabaseError('Get time entry IDs error', error)
    return []
  }
}

export async function getCloudTimeEntriesByIds(userId: string, ids: string[]): Promise<CloudTimeEntry[]> {
  if (!supabase || ids.length === 0) return []
  try {
    const { data, error } = await supabase
      .from('time_entries')
      .select('*')
      .eq('user_id', userId)
      .in('id', ids)
      .is('deleted_at', null)
    if (error) {
      logSupabaseError('Get time entries by IDs error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get time entries by IDs error', error)
    return []
  }
}

/**
 * Weich gelöschte Abschnitte seit `since`.
 *
 * `updated_at` steht mit Absicht in der Auswahl: `sync-pull` schiebt damit die
 * Wasserlinie weiter. Die älteren Entities lassen es weg, deren Linie bleibt
 * bei einem reinen Lösch-Fenster stehen und der nächste Pull holt es erneut —
 * verschwenderisch, nicht verlustig.
 */
export async function getDeletedCloudTimeEntries(userId: string, since?: Date): Promise<{ id: string; local_id: string | null; updated_at: string }[]> {
  if (!supabase) return []
  try {
    let query = supabase
      .from('time_entries')
      .select('id, local_id, updated_at')
      .eq('user_id', userId)
      .not('deleted_at', 'is', null)
    if (since) query = query.gte('deleted_at', since.toISOString())
    const { data, error } = await query
    if (error) {
      logSupabaseError('Get deleted time entries error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get deleted time entries error', error)
    return []
  }
}

export async function createCloudTimeEntry(
  userId: string,
  entry: Omit<CloudTimeEntry, 'id' | 'user_id' | 'created_at' | 'updated_at'>,
): Promise<CloudTimeEntry | null> {
  if (!supabase) return null
  try {
    const { data, error } = await supabase
      .from('time_entries')
      .insert({ ...entry, user_id: userId })
      .select()
      .single()
    if (error) {
      logSupabaseError('Create time entry error', error)
      return null
    }
    return data
  } catch (error) {
    logSupabaseError('Create time entry error', error)
    return null
  }
}

export async function updateCloudTimeEntry(
  entryId: string,
  updates: Partial<Omit<CloudTimeEntry, 'id' | 'user_id' | 'created_at'>>,
  userId?: string,
): Promise<{ updated_at: string } | null> {
  if (!supabase) return null
  try {
    let query = supabase
      .from('time_entries')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', entryId)
    if (userId) query = query.eq('user_id', userId)
    const { data, error } = await query.select('updated_at').single()
    if (error) {
      logSupabaseError('Update time entry error', error)
      return null
    }
    return data
  } catch (error) {
    logSupabaseError('Update time entry error', error)
    return null
  }
}

export async function deleteCloudTimeEntry(entryId: string, userId?: string): Promise<boolean> {
  if (!supabase) return false
  try {
    let query = supabase
      .from('time_entries')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', entryId)
    if (userId) query = query.eq('user_id', userId)
    const { error } = await query
    if (error) {
      logSupabaseError('Delete time entry error', error)
      return false
    }
    return true
  } catch (error) {
    logSupabaseError('Delete time entry error', error)
    return false
  }
}
